import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { CreateImproveAnswerDTO } from '../../chat/ImproveAnswer/service/types';
import { useSelector } from 'react-redux';
import {
  ImproveAnswerService,
  useMountSendMessage,
} from '../../chat/ImproveAnswer';
import { useTextAreaContext } from '../textArea/context';
import { useCopilotAnswer } from '../copilotAnswer/context';
import { useGlotaLocale } from 'src/hooks/useGlotaTranslation';

export type ImproveAnswerSettingsType = Pick<
  CreateImproveAnswerDTO,
  'tone' | 'context'
>;

type ImproveAnswerContextType = {
  isOpen: boolean;
  input: string;
  isLoading: boolean;
  error: string | null;
  improveAnswerSettings: ImproveAnswerSettingsType;
  handleOpen: () => void;
  handleClose: () => void;
  handleSendAnswer: () => void;
  handleCreateImprovedAnswer: () => void;
  retryRequest: () => void;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  setImproveAnswerSettings: React.Dispatch<
    React.SetStateAction<ImproveAnswerSettingsType>
  >;
};

const ImproveAnswerContext = createContext<
  ImproveAnswerContextType | undefined
>(undefined);

export function ImproveAnswerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { inputText: messageWrittenByUser } = useTextAreaContext();
  const { handleClose: handleCloseCopilot } = useCopilotAnswer();

  // store - redux
  const currentChat = useSelector((state: any) =>
    state?.chat?.chats?.find(
      (chat: any) => chat.id === state.chat.chatSelectedId
    )
  );

  const currentChannelId = currentChat?.chat?.channel_id;

  const currentMessages = useSelector(
    (state: any) =>
      state?.chat?.chats?.find(
        (chat: any) => chat.id === state.chat.chatSelectedId
      )?.messages
  );

  const userUuid = useSelector(
    (state: any) => state.chat?.config?.user?.uuid ?? null
  ) as string | null;

  const { backendLocale } = useGlotaLocale();

  const [isOpen, setIsOpen] = useState(false);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [improveAnswerSettings, setImproveAnswerSettings] =
    useState<ImproveAnswerSettingsType>({
      tone: 'FORMAL',
      context: [''],
    });

  const { handleMountSendMessage } = useMountSendMessage();

  const handleSendAnswer = useCallback(() => {
    setInput('');
    setIsOpen(false);
    handleMountSendMessage(input, currentChat);
  }, [currentChat?.id, input]);

  const handleCreateImprovedAnswer = useCallback(async () => {
    try {
      const contactUuid =
        currentChat?.contact?.uuid || currentChat?.contact?.contact_uuid;

      if (!userUuid) {
        console.error('uuid do usuário não encontrado');
        throw new Error('Uuid do usuário é inválido');
      }
      if (!contactUuid) {
        console.error('uuid do contato não encontrado');
        throw new Error('Uuid do contato é inválido');
      }

      setInput('');
      setIsLoading(true);
      setError(null);

      const payload = {
        message: messageWrittenByUser,
        tone: improveAnswerSettings.tone,
        context: improveAnswerSettings.context,
        contactUuid: contactUuid,
        userUuid: userUuid,
        channelId: currentChannelId,
        language: backendLocale,
      } as CreateImproveAnswerDTO;

      const response = await ImproveAnswerService.createImprovedAnswer(payload);

      setInput(response.improved_message);
      return response.improved_message;
    } catch (error) {
      setError(error as string);
    } finally {
      setIsLoading(false);
    }
  }, [
    improveAnswerSettings,
    userUuid,
    currentChat,
    messageWrittenByUser,
    currentChannelId,
    backendLocale,
  ]);

  const retryRequest = useCallback(() => {
    handleCreateImprovedAnswer();
  }, [
    currentMessages,
    currentChat?.id,
    messageWrittenByUser,
    currentChannelId,
  ]);

  // Reset quando muda de chat
  useEffect(() => {
    handleClose();
  }, [currentChat?.id]);

  useEffect(() => {
    const toneFromLocal = localStorage.getItem(
      'tone'
    ) as CreateImproveAnswerDTO['tone'];

    if (toneFromLocal) {
      setImproveAnswerSettings((prev) => ({
        ...prev,
        tone: toneFromLocal,
      }));
    }
  }, []);

  // Handle do click no botão para abrir Copilot
  const handleOpen = () => {
    if (!currentChat?.id) {
      return;
    }

    setIsOpen(true);
    retryRequest();
    handleCloseCopilot();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const value: ImproveAnswerContextType = {
    isOpen,
    input,
    isLoading,
    error,
    improveAnswerSettings,
    handleOpen,
    handleClose,
    handleSendAnswer,
    handleCreateImprovedAnswer,
    retryRequest,
    setInput,
    setImproveAnswerSettings,
  };

  return (
    <ImproveAnswerContext.Provider value={value}>
      {children}
    </ImproveAnswerContext.Provider>
  );
}

export function useImproveAnswer() {
  const context = useContext(ImproveAnswerContext);
  if (!context)
    throw new Error(
      'useImproveAnswer must be used within ImproveAnswerProvider'
    );
  return context;
}
